
import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { usePageConfig } from '../../../contexts/PageConfigContext';
import { oauthClientsApi, OAuthClientDto, RegisterClientRequestDto, RegisterClientResponseDto } from '../../../services/api';
import { OAuthClientsList } from './components/OAuthClientsList';
import { RegisterClientForm } from './components/RegisterClientForm';
import { CredentialsModal } from './components/CredentialsModal';
import { ClientDetailModal } from './components/ClientDetailModal';

export const OAuthClientsPage: React.FC = () => {
  const { setPageConfig } = usePageConfig();
  const [clients, setClients] = useState<OAuthClientDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Modal States
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<OAuthClientDto | null>(null);
  
  // Data for Credentials Modal
  const [newClientData, setNewClientData] = useState<RegisterClientResponseDto | null>(null);

  useEffect(() => {
    setPageConfig({
      title: 'Clientes OAuth',
      description: 'Gestión de credenciales de acceso para aplicaciones externas',
      actions: [
        {
          label: 'Registrar Cliente',
          // @ts-ignore - Dynamic icon capability
          icon: Plus,
          onClick: () => setShowRegisterModal(true),
          variant: 'primary'
        }
      ]
    });
    
    fetchClients();
  }, [setPageConfig]);

  const fetchClients = async () => {
    try {
      setIsLoading(true);
      const data = await oauthClientsApi.getAll();
      setClients(data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      // Could add toast notification here
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (data: RegisterClientRequestDto) => {
    try {
      setIsLoading(true); // Re-use main loading or local form loading?
      // Better to pass loading state to form, but simplicity here:
      const response = await oauthClientsApi.register(data);
      setNewClientData(response);
      setShowRegisterModal(false);
      setShowCredentialsModal(true);
      await fetchClients(); // Refresh list
    } catch (error) {
      console.error('Error registering client:', error);
      alert('Error al registrar el cliente. Por favor intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevoke = async (id: string) => {
    // Determine which client name to show
    const clientName = clients.find(c => c.id === id)?.name || 'este cliente';
    if (!window.confirm(`¿Estás seguro de que deseas revocar el acceso a "${clientName}"? Esta acción no se puede deshacer y la aplicación dejará de funcionar.`)) {
      return;
    }

    try {
      await oauthClientsApi.revoke(id);
      
      // Update local state or refresh
      setClients(prev => prev.map(c => 
        c.id === id ? { ...c, isActive: false } : c
      ));

      // Close detail modal if open
      if (selectedClient?.id === id) {
        setSelectedClient(prev => prev ? { ...prev, isActive: false } : null);
      }
    } catch (error) {
      console.error('Error revoking client:', error);
      alert('No se pudo revocar el cliente.');
    }
  };

  const handleView = async (id: string) => {
    const client = clients.find(c => c.id === id);
    if (client) {
      setSelectedClient(client);
    }
  };

  return (
    <div className="w-full">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <OAuthClientsList 
          clients={clients} 
          isLoading={isLoading && !showRegisterModal} // Don't show skeleton if just submitting form
          onView={handleView}
          onRevoke={handleRevoke}
        />
      </div>

      {/* Register Modal */}
      {showRegisterModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
            <div className="p-6 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-900">Registrar Nuevo Cliente</h3>
              <p className="text-slate-500 text-sm">Configura los permisos para la nueva aplicación</p>
            </div>
            <div className="p-6">
              <RegisterClientForm 
                onSubmit={handleRegister} 
                onCancel={() => setShowRegisterModal(false)}
                isLoading={isLoading}
              />
            </div>
          </div>
        </div>
      )}

      {/* Credentials Modal (Critical) */}
      {showCredentialsModal && newClientData && (
        <CredentialsModal 
          data={newClientData} 
          onClose={() => {
            setShowCredentialsModal(false);
            setNewClientData(null);
          }} 
        />
      )}

      {/* Detail Modal */}
      {selectedClient && (
        <ClientDetailModal
          client={selectedClient}
          onClose={() => setSelectedClient(null)}
          onRevoke={handleRevoke}
        />
      )}
    </div>
  );
};
